import { API_URL } from '../config/api';

import { AuthFetchError } from './auth-fetch-error';

interface AuthFetchOptions extends RequestInit {
  timeout?: number; // Timeout in milliseconds
}

/**
 * Wrapper around fetch that automatically includes authentication headers
 * @param url - The URL to fetch (can be relative to API_URL or absolute)
 * @param options - Standard fetch options plus optional timeout
 * @returns Promise<Response>
 */
export async function authFetch(url: string, options: AuthFetchOptions = {}): Promise<Response> {
  const { timeout, signal: externalSignal, ...fetchOptions } = options;
  // Get auth token from localStorage (try both keys for compatibility)
  const authToken = localStorage.getItem('jwt') || localStorage.getItem('auth_token');

  // Determine if URL is relative or absolute, avoid double /api prefix
  let fullUrl: string;
  if (url.startsWith('http')) {
    fullUrl = url;
  } else {
    // Strip /api prefix from url if API_URL already ends with /api
    let cleanUrl = url;
    if (API_URL.endsWith('/api') && url.startsWith('/api/')) {
      cleanUrl = url.substring(4); // Remove '/api' prefix
    } else if (API_URL.endsWith('/api') && url === '/api') {
      cleanUrl = ''; // Empty path if just '/api'
    }

    fullUrl = `${API_URL}${cleanUrl}`;
  }

  // Merge headers with auth token
  const headers = {
    ...options.headers,
    'Authorization': authToken ? `Bearer ${authToken}` : '',
    'Content-Type': 'application/json',
  };

  // Remove Content-Type for FormData
  if (fetchOptions.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  // #3269 iter-3 (codex CR-1 BLOCKING): honor an externally-supplied
  // AbortSignal (e.g. from a useEffect cleanup for scope-change race
  // cancellation) IN ADDITION to the internal timeout signal. Both
  // signals feed a single internal controller so `fetch` sees one
  // signal but either source can abort. On abort, disambiguate the
  // cause: external → re-throw AbortError so caller can silently
  // handle (per TOCTOU-AT-ASYNC-BOUNDARY-USE-CANCELLATION-NOT-CHECK);
  // internal timeout → throw the friendly "timed out" message.
  const controller = new AbortController();
  const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : null;
  // If the external signal is already aborted at call time, abort immediately.
  // Otherwise, subscribe once so external abort propagates to the internal
  // controller. `{ once: true }` prevents leaks if the fetch resolves first.
  const externalAbortHandler = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', externalAbortHandler, { once: true });
    }
  }

  try {
    const response = await fetch(fullUrl, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });

    if (timeoutId) clearTimeout(timeoutId);
    if (externalSignal) externalSignal.removeEventListener('abort', externalAbortHandler);

    // Handle 401 Unauthorized - redirect to login
    if (response.status === 401) {
      localStorage.removeItem('jwt');
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
      throw new AuthFetchError('Unauthorized - redirecting to login', response);
    }

    return response;
  } catch (error: any) {
    if (timeoutId) clearTimeout(timeoutId);
    if (externalSignal) externalSignal.removeEventListener('abort', externalAbortHandler);
    if (error.name === 'AbortError') {
      // Disambiguate: external-caller abort (scope change etc.) re-throws
      // AbortError verbatim so the caller can silently discard. Internal
      // timeout throws the friendly retry message (existing contract).
      if (externalSignal && externalSignal.aborted) {
        throw error;
      }
      throw new Error('Request timed out. The operation is still running in the background - please refresh the page in a moment to see results.');
    }
    throw error;
  }
}

/**
 * Helper to check if response is ok and parse JSON
 * @param response - Fetch response
 * @returns Parsed JSON data
 */
export async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }
  
  return response.json();
}